import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import {
  getPublicLinksForRecordGroup,
  createPublicLink,
  updatePublicLink,
  togglePublicLink,
  deletePublicLink,
} from '@/services/api/form.service'
import type { CreatePublicLinkRequest } from '@/types/api/form'

import { useWorkspace } from './useWorkspace'

export function useFormPublicLinks(formId: string) {
  const { currentWorkspaceId } = useWorkspace()
  const queryClient = useQueryClient()
  const queryKey = ['workspace', currentWorkspaceId, 'forms', formId, 'public-links']

  const query = useQuery({
    queryKey,
    queryFn: () => getPublicLinksForRecordGroup(formId),
    enabled: !!formId && !!currentWorkspaceId,
    staleTime: 30 * 1000,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey })

  const createMutation = useMutation({
    mutationFn: createPublicLink,
    onSuccess: invalidate,
  })

  const updateMutation = useMutation({
    mutationFn: ({ linkId, data }: { linkId: string; data: Partial<CreatePublicLinkRequest> }) =>
      updatePublicLink(linkId, data),
    onSuccess: invalidate,
  })

  const toggleMutation = useMutation({
    mutationFn: togglePublicLink,
    onSuccess: invalidate,
  })

  const deleteMutation = useMutation({
    mutationFn: deletePublicLink,
    onSuccess: invalidate,
  })

  return {
    links: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    create: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    update: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    toggle: toggleMutation.mutateAsync,
    isToggling: toggleMutation.isPending,
    remove: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  }
}
